"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { cn } from "../../utils";
import type { SolidShapeVariantProps, SolidShapeSvgProps } from "../solid-shape.types";
import { colorMap } from "../colors";
function DiamondSvg(t0) {
  const $ = _c(5);
  const {
    color,
    animated,
    shapeScale
  } = t0;
  const t1 = colorMap[color];
  const t2 = animated ? shapeScale : 1;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      scale: t2,
      transformOrigin: "center center"
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t3) {
    t4 = <svg className="absolute inset-0 h-full w-full" viewBox="0 0 240 240" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true"><motion.path d="M120 0L0 120L120 240L240 120L120 0Z" fill={t1} style={t3} /></svg>;
    $[2] = t1;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
const DiamondSolidShape = React.forwardRef<HTMLDivElement, SolidShapeVariantProps>((t0, ref) => {
  const $ = _c(26);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      color: t1,
      animated: t2,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const color = t1 === undefined ? "bright-blue" : t1;
  const animated = t2 === undefined ? true : t2;
  const containerRef = useRef(null);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      target: containerRef,
      offset: ["start end", "end start"]
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    scrollYProgress
  } = useScroll(t3);
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, 1];
    t5 = [1, 1.3];
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  const shapeScale = useTransform(scrollYProgress, t4, t5);
  let t6;
  if ($[9] !== ref) {
    t6 = node => {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
    };
    $[9] = ref;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = !children && "aspect-square";
  let t8;
  if ($[11] !== className || $[12] !== t7) {
    t8 = cn("relative overflow-hidden", t7, className);
    $[11] = className;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== animated || $[15] !== color || $[16] !== shapeScale) {
    t9 = <DiamondSvg color={color} animated={animated} shapeScale={shapeScale} />;
    $[14] = animated;
    $[15] = color;
    $[16] = shapeScale;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== children) {
    t10 = children && <div className="relative z-10">{children}</div>;
    $[18] = children;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== props || $[21] !== t10 || $[22] !== t6 || $[23] !== t8 || $[24] !== t9) {
    t11 = <div ref={t6} data-slot="solid-shape" className={t8} {...props}>{t9}{t10}</div>;
    $[20] = props;
    $[21] = t10;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
});
DiamondSolidShape.displayName = "DiamondSolidShape";
export { DiamondSolidShape };